import { useMemo } from 'react';
import { useQuery } from 'urql';

import {
  selectImpersonationOverrides,
  useImpersonationSelector,
} from '@revelio/auth';
import { GetReportConfig } from '@revelio/data-access';

import {
  generateReportEntityMap,
  type IReportEntityMap,
  SpecialReportCategories,
} from '../report-builder/02-entity-configuration/mw-reports/utils';

const getCategories = (config: IReportEntityMap) => {
  // Categories determine which groups the reports will be in. There are two sepecial categories:
  // * Featured: This category will always appear on top of the list
  // * Additional: Report configrations that don't have any categories will be grouped into this category which appears at the bottom of the list
  if (!config) return [];
  const unsortedCategories = Array.from(
    new Set(
      // biome-ignore lint/complexity/useFlatMap: migrated from develop
      Object.values(config)
        .map((config) =>
          config.categories && config.categories.length > 0
            ? config.categories
            : [SpecialReportCategories.Additional]
        )
        .flat()
    )
  ) as string[];
  return unsortedCategories.sort((a, b) => {
    const order: Record<string, number> = {
      [SpecialReportCategories.Featured]: -1,
      [SpecialReportCategories.Additional]: 1,
    };
    return (order[a] || 0) - (order[b] || 0) || a.localeCompare(b);
  });
};

export const useReportBuilderConfig = () => {
  const overrides = useImpersonationSelector(selectImpersonationOverrides);
  const [{ data, fetching: loading, error }] = useQuery({
    query: GetReportConfig,
  });

  const config: IReportEntityMap = useMemo(() => {
    if (!data?.reports_validation) {
      return {};
    }

    const allowedReportTypes = overrides?.reportTypes;
    const filteredReports =
      allowedReportTypes !== undefined
        ? data.reports_validation.filter((report) =>
            report?.report_validation?.report_type
              ? allowedReportTypes.includes(
                  report.report_validation.report_type
                )
              : false
          )
        : data.reports_validation;

    return generateReportEntityMap(filteredReports);
  }, [data, overrides?.reportTypes]);

  const categories = useMemo(() => {
    return getCategories(config);
  }, [config]);

  return {
    config,
    categories,
    loading,
    error,
  };
};
